import request from '@/api/request';
import * as wellsRappers from '@/restore/well-logs/rappers/wells-rappers';
import * as clicks from '@/restore/well-logs/rappers/clicks';

/*
    https://redux.js.org/usage/structuring-reducers/normalizing-state-shape
*/

/*
    Общая модель для работы с реперами
*/

const initialState = {
    /*
        versions - это пользовательские наборы, которые содержат список реперов (при создании репера он закрепляется за конкретным набором)
    */
    versions: null,
    /*
        словарь всех созданных реперов
    */
    rappers: null,

    isLoading: false,
    error: null,

    /*
        Активная версия (id)
    */
    activeVersionID: null,

    /*
        Активный пакет (id)
    */
    activePackID: null,

    /*
        Вспомогательное поле - значение для сохранения параметров запроса.
        Эти параметры сравниваются с текущими, на основании чего делается вывод о необходимости повторного запроса.
    */
    prevRequestParams: [],
};

export const rappersModel = {
    name: 'rappers',
    state: initialState,
    reducers: {
        setResponseData(state, payload) {
            const { packVersions, packs } = payload;

            return { ...state, versions: packVersions, rappers: packs, isLoading: false };
        },
        startRequest(state) {
            return { ...state, isLoading: true, error: null };
        },
        setError(state, error) {
            const message = error?.message ?? String(error);
            return { ...state, isLoading: false, error: message, versions: null, rappers: null };
        },
        setIsLoading(state, payload) {
            return { ...state, isLoading: payload };
        },
        setActiveVersionID(state, activeID) {
            return { ...state, activeVersionID: activeID, activePackID: null };
        },
        setActivePackID(state, activeID) {
            return { ...state, activePackID: activeID };
        },
        setRequestParams(state, params) {
            return { ...state, prevRequestParams: params };
        },
        addVersionData(state, payload) {
            return { ...state, versions: [payload, ...(state.versions ?? [])], isLoading: false };
        },
        updateVersionData(state, payload) {
            const versions = (state.versions ?? []).map(version => {
                if (version?.versionId === payload.versionId) {
                    return payload;
                }

                return version;
            });

            return { ...state, versions, isLoading: false };
        },
        removeVersionData(state, payload) {
            const versions = (state.versions ?? []).filter(x => x.versionId !== payload);
            /*
                Если удаляемый набор была активным, то его надо сбросить.
            */
            const activeVersionID = payload === state.activeVersionID ? null : state.activeVersionID;

            return { ...state, versions, activeVersionID, isLoading: false };
        },
        addPackData(state, payload) {
            const versions = (state.versions ?? []).map(version => {
                if (version?.versionId === payload.versionId) {
                    return { ...version, packs: [payload.packID, ...version.packs] };
                }

                return version;
            });

            return { ...state, versions, rappers: { ...state.rappers, [payload.packID]: payload }, isLoading: false };
        },
        updatePackData(state, payload) {
            return { ...state, rappers: { ...state.rappers, [payload.packID]: payload }, isLoading: false };
        },
        removePackData(state, payload) {
            const versions = (state.versions ?? []).map(version => {
                if (version?.versionId === payload.versionId) {
                    const packs = version.packs.filter(pack => pack.packID !== payload.packID);

                    return { ...version, packs };
                }

                return version;
            });

            /*
                Если удаляемая версия репера была активной, то ее надо сбросить.
            */
            const activePackID = payload.packID === state.activePackID ? null : state.activePackID;

            const rappers = { ...state.rappers };
            delete rappers[payload.packID];

            return { ...state, versions, rappers, activePackID, isLoading: false };
        },
    },
    effects: dispatch => ({
        setActivePackIdEffect(packID) {
            dispatch.rappers.setActivePackID(packID);
            clicks.resetClicks();
        },
        async getVersions(_, rootState) {
            if (!rootState.rappers?.rappers) {
                this.setIsLoading(true);
                const { error, prevRequestParams } = rootState.rappers;
                const { isGroup, activeTabletId, activeGroupIds } = rootState.tablets;

                let wellIDs = [];

                if (activeTabletId) {
                    wellIDs.push(activeTabletId);
                }

                // Данное условие не может быть выполнено, потому что isGroup всегда false,
                // в системе нет вызова редьюсера, который бы изменил значение на true.
                if (isGroup) {
                    wellIDs = wellIDs.concat(activeGroupIds);
                }

                /*
                Повторный запрос происходит при условии, что предыдущий НЕ завершился ошибкой и предыдущие параметры
                совпадают с текущими.
            */
                const isEqualParams = !error && arrayEquals(prevRequestParams, wellIDs);

                // if (isEqualParams) return; TODO пока не разберемся почему при открытие реперов isEqualParams = true

                this.setRequestParams(wellIDs);
                this.startRequest();

                /*
                body: {
                    wellId: [wellID, wellID, ..., wellID],
                }
            */
                try {
                    /*
                    Fake request (for test) -> NODE server

                    const response = await fetch('/api/well-packs', {
                        method: 'POST',
                        body: JSON.stringify({
                            wellId: wellIDs,
                        }),
                    });
                */

                    const response = await request.post(`logging/packs-manager/`, {
                        body: JSON.stringify({
                            wellId: wellIDs,
                        }),
                    });

                    const { packVersions, packs: rawPacks } = await response.json();

                    const packs = Object.entries(rawPacks).reduce((result, [packID, value]) => {
                        result[packID] = {
                            ...value,
                            packID: value.packID ?? +packID,
                        };
                        return result;
                    }, {});

                    this.setResponseData({ packVersions, packs });
                } catch (error) {
                    this.setError(error);
                }
                this.setIsLoading(false);
            }
        },
        async addVersion(payload) {
            this.setIsLoading(true);
            this.startRequest();

            try {
                const response = await request.post(`logging/add-pack-version/`, {
                    body: JSON.stringify(payload),
                });

                const data = await response.json();

                const result = {
                    ...payload,
                    date: new Date().toISOString().slice(0, 19),
                    locked: 0,
                    versionId: data.id,
                    isAvailable: true,
                    packs: [],
                };

                this.addVersionData(result);
            } catch (error) {
                this.setError(error);
            }
            this.setIsLoading(false);
        },
        async updateVersion(payload) {
            this.setIsLoading(true);
            this.startRequest();

            try {
                await request.post(`logging/change-pack-version-title/`, {
                    body: JSON.stringify(payload),
                });

                this.updateVersionData(payload);
            } catch (error) {
                this.setError(error);
            }
        },
        async removeVersion(payload, rootState) {
            const activeVersionID = rootState.rappers.activeVersionID;

            this.startRequest();

            try {
                await request.get(`logging/delete-pack-data/?versionId=${payload}`);

                this.removeVersionData(payload);

                /*
                    Удаления связанных с набором rappers
                */
                if (activeVersionID === payload) {
                    wellsRappers.removeAllRappers();
                }
            } catch (error) {
                this.setError(error);
            }
            this.setIsLoading(false);
        },
        async addPack(payload, rootState) {
            this.setIsLoading(true);
            this.startRequest();

            try {
                const body = {
                    ...payload,
                    versionId: rootState.rappers.activeVersionID,
                };

                const response = await request.post(`logging/add-work-pack/`, {
                    body: JSON.stringify(body),
                });

                const data = await response.json();

                const result = {
                    ...body,
                    packID: data.id,
                };

                this.addPackData(result);
            } catch (error) {
                this.setError(error);
            }
            this.setIsLoading(false);
        },
        async updatePack(payload) {
            this.setIsLoading(true);
            this.startRequest();

            try {
                await request.post(`logging/change-work-pack/`, {
                    body: JSON.stringify(payload),
                });

                this.updatePackData(payload);
            } catch (error) {
                this.setError(error);
            }
            this.setIsLoading(false);
        },
        async removePack(payload) {
            this.setIsLoading(true);
            this.startRequest();

            try {
                await request.get(`logging/delete-pack-data/?packID=${payload.packID}`);

                this.removePackData(payload);
                /*
                    Удаления связанных с версией rappers на скважине
                */
                wellsRappers.removeRappersByPackId(payload.packID);
            } catch (error) {
                this.setError(error);
            }
            this.setIsLoading(false);
        },
        async copyPacks(payload) {
            const { versionId, packIds, wellIds } = payload;
            this.setIsLoading(true);
            this.startRequest();
            try {
                await request.post(`logging/copy-work-packs/`, {
                    body: JSON.stringify(payload),
                });

                //Копирование кода из инициализации тк, там много во время копирования меняется и ID известны только серверу
                this.setRequestParams(wellIds);
                console.log('copyPacks');
                const response = await request.post(`logging/packs-manager/`, {
                    body: JSON.stringify({
                        wellId: wellIds,
                    }),
                });

                const { packVersions, packs: rawPacks } = await response.json();

                const packs = Object.entries(rawPacks).reduce((result, [packID, value]) => {
                    result[packID] = {
                        ...value,
                        packID: value.packID ?? +packID,
                    };
                    return result;
                }, {});

                this.setResponseData({ packVersions, packs });
                wellsRappers.getWellsRappers(true);

                // await dispatch.wellsRappers.getWellsRappers();
            } catch (error) {
                this.setError(error);
            }
            this.setIsLoading(false);
        },
    }),
};

function arrayEquals(a, b) {
    return a.length === b.length && a.every((val, index) => val === b[index]);
}
